import React from 'react';
import { Progress } from 'antd';
import { Store, ClipboardCheck, Clock, TriangleAlert, Trophy, CircleCheck, CircleX, TrendingUp } from 'lucide-react';
import { StatCard } from '../../../common';
import { formatNumber } from '../../../../utils/format';
import { semantic } from '../../../../theme/tokens';
import { useClientDetailAnalytics } from './clientDetailAnalytics.controller';

const unit = (value, suffix) => (
    <>
        {value}
        <span className="ml-1 text-base font-medium text-surface-500">{suffix}</span>
    </>
);

export default function ClientDetailAnalytics({ client, stores, recentAudits }) {
    const analytics = useClientDetailAnalytics(stores, recentAudits);
    const hasMissing = analytics.missingProductsCount > 0;

    return (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
            <StatCard
                label="Pisos de Venta"
                value={formatNumber(analytics.totalStores)}
                icon={<Store />}
                tone="brand"
                hint={`${formatNumber(analytics.activeStores)} activos · ${formatNumber(analytics.inactiveStores)} inactivos`}
            />
            <StatCard
                label="Auditorías"
                value={formatNumber(analytics.totalAudits)}
                icon={<ClipboardCheck />}
                tone="success"
                hint={`${formatNumber(analytics.recentActivity)} en últimos 7 días`}
                trend={
                    analytics.recentActivity > 0 ? (
                        <span className="inline-flex items-center gap-1 text-success-fg">
                            <TrendingUp />
                            {`+${analytics.recentActivity}`}
                        </span>
                    ) : null
                }
            />
            <StatCard
                label="Cobertura"
                help="Pisos de venta con al menos una auditoría en el período seleccionado."
                value={unit(analytics.coveragePercent, '%')}
                valueTone={analytics.coverageTone}
                icon={<Trophy />}
                tone={analytics.coverageTone}
                footer={
                    <>
                        <Progress
                            percent={analytics.coveragePercent}
                            showInfo={false}
                            strokeColor={semantic[analytics.coverageTone].solid}
                            size={['100%', 6]}
                            className="!mb-1"
                        />
                        <span className="text-xs text-surface-500 tabular-nums">
                            {formatNumber(analytics.uniqueStoresAudited)} de {formatNumber(analytics.totalStores)} pisos auditados
                        </span>
                    </>
                }
            />
            <StatCard label="Tiempo Promedio" value={unit(formatNumber(analytics.avgAuditTime), 'min')} icon={<Clock />} tone="info" hint="Por auditoría" />
            <StatCard
                label="Productos Faltantes"
                value={formatNumber(analytics.missingProductsCount)}
                valueTone={hasMissing ? 'danger' : 'success'}
                icon={<TriangleAlert />}
                tone={hasMissing ? 'danger' : 'success'}
                hint="En período seleccionado"
            />
            <StatCard
                label="Estado del Cliente"
                value={client?.active ? 'Activo' : 'Inactivo'}
                valueTone={client?.active ? 'success' : 'danger'}
                icon={client?.active ? <CircleCheck /> : <CircleX />}
                tone={client?.active ? 'success' : 'danger'}
                hint={client?.active ? 'Cliente operativo' : 'Cliente suspendido'}
            />
        </div>
    );
}
